import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, Download, Loader2, Linkedin, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const mediaUrl = (p) => (p ? `${API}/media/${p}` : null);

// LinkedIn PDF carousel: slide-by-slide preview, edit the 3 content slides, download, or queue as a document post.
export const CarouselModal = ({ open, onOpenChange, cid, card, onChange, preview = false }) => {
  const [i, setI] = useState(0);
  const [slides, setSlides] = useState(card.slides || []);
  const [busy, setBusy] = useState(false);
  const [queuing, setQueuing] = useState(false);
  const paths = card.slide_paths || [];

  useEffect(() => { if (open) { setSlides(card.slides || []); setI(0); } }, [open, card.slides]);

  const rebuild = async (edited) => {
    setBusy(true);
    try {
      const r = await api.post(`/campaign/${cid}/card/${card.id}/carousel`, edited ? { slides } : {});
      onChange({ ...card, slides: r.data.slides, slide_paths: r.data.slide_paths, pdf_path: r.data.pdf_path,
        image_path: r.data.image_path, visual_style: "carousel" });
      toast.success("Carousel PDF rebuilt");
    } catch (e) { toast.error("Could not build the carousel"); }
    finally { setBusy(false); }
  };

  const queue = async () => {
    setQueuing(true);
    try {
      const r = await api.post(`/campaign/${cid}/card/${card.id}/queue-carousel`);
      onChange({ ...card, queued_post_id: r.data.post_id });
      toast.success("Queued as a LinkedIn carousel", { description: r.data.scheduled_at ? new Date(r.data.scheduled_at).toLocaleString() : "" });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not queue the carousel"));
    } finally { setQueuing(false); }
  };

  const setSlide = (k, field, v) => setSlides((s) => s.map((x, j) => (j === k ? { ...x, [field]: v } : x)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-card border-border max-h-[92vh] overflow-y-auto" data-testid="carousel-modal">
        <DialogHeader><DialogTitle className="font-display">LinkedIn PDF Carousel · 5 slides (1080×1350)</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-[1fr_1.1fr] gap-5">
          <div>
            <div className="relative aspect-[4/5] rounded-lg overflow-hidden bg-secondary/40 border border-border">
              {busy || !paths.length ? (
                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : (
                <img src={mediaUrl(paths[i])} alt={`Slide ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" data-testid={`carousel-slide-img-${i}`} />
              )}
              <button onClick={() => setI((x) => Math.max(0, x - 1))} disabled={i === 0} data-testid="carousel-prev"
                className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => setI((x) => Math.min(paths.length - 1, x + 1))} disabled={i >= paths.length - 1} data-testid="carousel-next"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <div className="flex gap-1.5 mt-2" data-testid="carousel-thumbs">
              {paths.map((p, k) => (
                <button key={p} onClick={() => setI(k)} className={`flex-1 aspect-[4/5] rounded overflow-hidden border ${k === i ? "border-primary" : "border-border opacity-60"}`}>
                  <img src={mediaUrl(p)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Slide 1 is the cover hook, slides 2–4 carry one spec each, slide 5 is the CTA. LinkedIn publishes the PDF as a swipeable document; Facebook gets the cover slide.</p>
            {slides.map((s, k) => (
              <div key={k} className="rounded-lg border border-border p-2.5 space-y-1.5">
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Slide {k + 2}</span>
                <Input value={s.title} onChange={(e) => setSlide(k, "title", e.target.value)} className="h-8 text-sm bg-secondary/40" data-testid={`carousel-slide-title-${k}`} />
                <Input value={s.body} onChange={(e) => setSlide(k, "body", e.target.value)} className="h-8 text-xs bg-secondary/40" data-testid={`carousel-slide-body-${k}`} />
              </div>
            ))}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => rebuild(true)} disabled={busy} data-testid="carousel-rebuild-btn" className="border-border">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Rebuild slides
              </Button>
              {card.pdf_path && (
                <a href={mediaUrl(card.pdf_path)} target="_blank" rel="noreferrer" download data-testid="carousel-download-pdf">
                  <Button size="sm" variant="outline" className="border-border"><Download className="h-3.5 w-3.5 mr-1.5" /> Export PDF</Button>
                </a>
              )}
              {!preview && (
                <Button size="sm" onClick={queue} disabled={queuing || !card.pdf_path || !!card.queued_post_id} data-testid="carousel-queue-btn"
                  className="bg-[#0A66C2] hover:bg-[#0A66C2]/90 text-white">
                  {queuing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : card.queued_post_id ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Linkedin className="h-3.5 w-3.5 mr-1.5" />}
                  {card.queued_post_id ? "Queued on LinkedIn" : "Queue as LinkedIn Carousel PDF"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
